import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import type {
    OnboardingChangesetActions,
    OnboardingChangesetState,
    OnboardingChangesetStore,
} from './onboarding-changeset-store';
import { createOnboardingChangesetStore } from './onboarding-changeset-store';

export const OnboardingChangesetStoreContext = createContext<StoreApi<OnboardingChangesetStore> | null>(null);

type OnboardingChangesetProviderProps = PropsWithChildren<{
    readonly architectureId?: string;
}>;

export const OnboardingChangesetProvider: FunctionComponent<OnboardingChangesetProviderProps> = ({
    architectureId,
    children,
}) => {
    const storeRef = useRef<StoreApi<OnboardingChangesetStore> | null>(null);
    const prevArchitectureIdRef = useRef(architectureId);

    storeRef.current ??= createOnboardingChangesetStore();

    useEffect(() => {
        if (architectureId !== undefined && prevArchitectureIdRef.current !== architectureId) {
            prevArchitectureIdRef.current = architectureId;
            storeRef.current?.getState().clear();
        }
    }, [architectureId]);

    return (
        <OnboardingChangesetStoreContext.Provider value={storeRef.current}>
            {children}
        </OnboardingChangesetStoreContext.Provider>
    );
};

const useChangesetStoreContext = (): StoreApi<OnboardingChangesetStore> => {
    const store = useContext(OnboardingChangesetStoreContext);

    if (store === null) {
        throw new Error('useChangesetState/useChangesetActions must be used within OnboardingChangesetProvider');
    }

    return store;
};

export const useChangesetState = (): OnboardingChangesetState => {
    const store = useChangesetStoreContext();

    return useStore(
        store,
        useShallow((state) => ({
            addedRows: state.addedRows,
            removedIds: state.removedIds,
            selectedIds: state.selectedIds,
            updatedById: state.updatedById,
        })),
    );
};

export const useChangesetActions = (): OnboardingChangesetActions => {
    const store = useChangesetStoreContext();

    return useMemo(
        () => ({
            applyRegenerationResult: store.getState().applyRegenerationResult,
            clear: store.getState().clear,
            removeItem: store.getState().removeItem,
            toggleSelection: store.getState().toggleSelection,
        }),
        [store],
    );
};
